import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { usePhishPass } from '../../context/PhishPassContext';
import { Loader2, Save, Settings as SettingsIcon } from 'lucide-react';

const PhishPassSettings = () => {
  const { getSettings, updateSettings } = usePhishPass();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => { const s = await getSettings(); setSettings(s); })(); }, [getSettings]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    const payload = {
      sender_email: settings.sender_email,
      sender_display_name: settings.sender_display_name,
      high_risk_threshold: Math.max(1, parseInt(settings.high_risk_threshold) || 3),
    };
    const r = await updateSettings(payload);
    setSaving(false);
    if (r.success) { toast.success('Settings saved'); setSettings(r.data); } else toast.error(r.error);
  };

  if (!settings) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="phishpass-settings">
      <div className="flex items-center gap-2">
        <SettingsIcon className="w-5 h-5 text-slate-600" />
        <h2 className="text-lg font-semibold text-slate-900">PhishPass Settings</h2>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <Label className="text-sm font-semibold">Default Sender Email</Label>
            <p className="text-xs text-slate-500 mb-2">
              The &quot;From:&quot; address for phishing simulation emails. Must be a verified domain in Resend.
              When you set up a separate &quot;external-looking&quot; Resend domain (e.g. m365-secure.com), paste it here.
            </p>
            <Input
              value={settings.sender_email}
              onChange={e => setSettings({ ...settings, sender_email: e.target.value })}
              data-testid="sender-email-input"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">Default Sender Display Name</Label>
            <p className="text-xs text-slate-500 mb-2">Used when a template doesn&apos;t specify its own display name.</p>
            <Input
              value={settings.sender_display_name}
              onChange={e => setSettings({ ...settings, sender_display_name: e.target.value })}
              data-testid="sender-name-input"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">High-Risk Threshold (clicks across campaigns)</Label>
            <p className="text-xs text-slate-500 mb-2">
              When an employee clicks this many or more phishing emails total, fire a HIGH_RISK_EMPLOYEE_DETECTED alert.
              Configure admins to receive this alert at <code className="bg-slate-100 px-1 rounded">/admin/settings</code> → PhishPass.
            </p>
            <Input
              type="number"
              min="1"
              value={settings.high_risk_threshold}
              onChange={e => setSettings({ ...settings, high_risk_threshold: e.target.value })}
              data-testid="threshold-input"
              className="w-32"
            />
          </div>

          <div className="pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-1" />Save Settings</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhishPassSettings;
